import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  isDevMode,
  LOCALE_ID,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { sentryHttpInterceptor } from './features/monitoring/interceptors/sentry-http.interceptor';
import { environment } from '../environments/environment';
import { ApiConfiguration as OmniCoreConfiguration } from './core/clients/omni-core/api-configuration';
import { ApiConfiguration as SignatureConfiguration } from './core/clients/signature/api-configuration';
import { ApiConfiguration as PortalConfiguration } from './core/clients/portal/api-configuration';
import { provideIcons } from '@ng-icons/core';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { IDENTITY } from './core/injection-tokens/id-token.injection-token';
import { provideMockUserInfo } from './core/injection-tokens/mock-user-info.injection-token';
import { getAccessToken, getIdTokenPayload } from './core/helper/cookie';
import { COMMON_ICONS } from 'excellence-ui';
import { provideCustomEventPlugins } from './core/event-plugins/provider';
import { UserInfoService } from './features/user/services/user-info.service';
import { provideSentry } from './features/monitoring/providers/sentry.providers';
import { provideHotjar } from './features/monitoring/providers/hotjar.providers';
import { provideEChartsTheme } from './core/providers/echarts-theme.provider';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { ConfigService } from './core/services/config.service';

registerLocaleData(localeDe);

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'de' },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([sentryHttpInterceptor])),
    provideIcons(COMMON_ICONS),
    {
      provide: OmniCoreConfiguration,
      useValue: ((): OmniCoreConfiguration => ({ rootUrl: environment.omniCoreApiUrl }))(),
    },
    {
      provide: SignatureConfiguration,
      useValue: ((): SignatureConfiguration => ({ rootUrl: environment.signatureApiUrl }))(),
    },
    {
      provide: PortalConfiguration,
      useValue: ((): PortalConfiguration => ({ rootUrl: environment.portalApiUrl }))(),
    },
    {
      provide: IDENTITY,
      useFactory: () => getIdTokenPayload() ?? null,
    },
    provideMockUserInfo(environment.mockUserInfo),
    provideAppInitializer(async () => {
      const configService = inject(ConfigService);
      const userService = inject(UserInfoService);

      const { demoMode } = await configService.load();

      if (!getAccessToken() && !demoMode) {
        return;
      }
      await userService.load();
    }),
    provideTransloco({
      config: {
        availableLangs: ['de', 'en'],
        defaultLang: 'de',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideCustomEventPlugins(),
    ...provideSentry(),
    ...provideHotjar(),
    provideEChartsTheme(),
  ],
};
